import { getItem } from 'app/utils/getItem';

export {
    trackAddToBagFromMiniPDP,
    trackAddToWishlistFromMiniPDP,
    trackViewMoreDetails,
    trackAddToBagError,
    trackAddToWishlistError,
    trackGoToBag,
    trackAddToBagNonBasketProduct,
    trackRemoveRecentlyViewedItem,
} from '@ynap/product-details-page';

export const trackSizeGuideOpen = () => ({
    event: 'size and fit - open',
    effect: 'open tool',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeGuideClose = () => ({
    event: 'size and fit - close',
    effect: 'close tool',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeGuideComplete = () => ({
    event: 'size and fit - complete',
    effect: 'complete tool steps',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeSelect = (analytics, sizeSku, sizeLabel, internationalSizes) => ({
    event: 'size selection',
    category: {
        primaryCategory: 'product page',
    },
    item: getItem(analytics?.product?.[0], sizeSku, sizeLabel, null, null, internationalSizes),
});

export const trackSizeAndFitSizeSelect = () => ({
    event: 'size and fit - size selection',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackAccordion = (analytics, accordion) => ({
    event: `open product accordion - ${accordion}`,
    effect: 'view more content',
    category: {
        primaryCategory: 'product page',
    },
    item: getItem(analytics?.product?.[0]),
});

export const trackAccordionLinkClick = (analytics, targetURL) => ({
    event: 'product accordion - link click',
    effect: 'click on a link inside the product accordion',
    category: {
        primaryCategory: 'product page',
        subCategory: `${targetURL}`,
    },
    item: getItem(analytics?.product?.[0]),
});

export const trackSizeGuide = (analytics) => ({
    event: 'size and fitting guide',
    effect: 'new window',
    category: {
        primaryCategory: 'product page',
        subCategory: 'open window',
    },
    item: getItem(analytics?.product?.[0]),
});

export const trackImage = (analytics, interaction) => ({
    event: 'browse product images',
    effect: interaction,
    category: {
        primaryCategory: 'product page',
        subCategory: interaction,
    },
    item: getItem(analytics?.product?.[0]),
});

export const trackAddToWishlist = (analytics, sizeSku, sizeLabel, internationalSizes) => {
    const { category = {} } = analytics.page || {};
    return {
        event: 'wish list - add item',
        effect: 'add item to wish list',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: getItem(analytics?.product?.[0], sizeSku, sizeLabel, true, null, internationalSizes),
    };
};

export const trackOriginalOutfitProducts = (productDetails, position, orginalProductId) => ({
        event: 'shown here with click',
        eventEffect: '',
        item: [
            {
                attributes: {
                    badges: productDetails?.badge,
                    crossSellItem: orginalProductId,
                    discountPercent: productDetails?.price?.discountPercentage || 0,
                    position: position,
                    productFindingMethod: '$rv - module',
                    saleFlag: productDetails?.price?.discountPercentage ? 'on sale' : 'full price',
                    stock: productDetails?.visible ? 'IN_STOCK' : 'OUT_STOCK',
                    sustainabilityFlag: productDetails?.badge?.key === 'BADGE_NET_SUSTAIN',
                },
                category: {
                    primaryCategory: productDetails?.masterCategory?.label,
                    subCategory1: productDetails?.masterCategory?.child?.label,
                    subCategory2: productDetails?.masterCategory?.child?.child?.label,
                },
                price: {
                    basePrice: productDetails?.price?.originalPrice,
                    currency: productDetails?.price?.currency?.label,
                    discount: productDetails?.price?.discountPercentage,
                    discountPrice: productDetails?.price?.originalPrice ? productDetails.price.originalPrice - productDetails.price.finalPrice : 0,
                    priceWithDiscountTax: productDetails?.price?.finalPrice,
                },
                productInfo: {
                    designer: productDetails?.designer,
                    designerID: productDetails?.designerIdentifier,
                    name: productDetails?.description,
                    productID: productDetails?.partNumber,
                },
            },
        ],
    });

export const upsellNavigation = (analytics, recommendedProduct, position, event, productFindingMethod) => {
    const crossSellItem = analytics?.product?.[0];
    const gender = crossSellItem?.productInfo?.gender;
    const { price, masterCategory } = recommendedProduct;

    const stockMap = {
        InStock: 'IN_STOCK',
        SoldOut: 'SOLD_OUT',
        InStoreOnly: 'IN_STORE_ONLY',
    };

    return {
        event,
        effect: 'go to product page',
        category: {
            primaryCategory: 'upselling',
            subCategory: 'product page',
        },
        attributes: {
            position,
        },
        item: [
            {
                attributes: {
                    productFindingMethod,
                    crossSellItem: crossSellItem?.productInfo?.productID,
                    sustainabilityFlag: crossSellItem?.attributes.sustainabilityFlag,
                    badges: recommendedProduct?.badge,
                    discountPercent: price?.discountPercentage,
                    position,
                    saleFlag: price?.discountPercentage > 0 ? 'on sale' : 'full price',
                    stock: stockMap[price?.availability] || price?.availability,
                },
                category: {
                    primaryCategory: masterCategory?.labelEN || masterCategory?.label,
                    subCategory1: masterCategory?.child?.labelEN || masterCategory?.child?.label,
                    subCategory2: masterCategory?.child?.child?.labelEN || masterCategory?.child?.child?.label,
                },
                price: {
                    basePrice: price?.originalPrice || price?.finalPrice,
                    discount: price?.discountPercentage || 0,
                    discountPrice: price?.originalPrice ? price?.originalPrice - price?.finalPrice : 0,
                    priceWithDiscountTax: price?.finalPrice,
                    currency: price?.currency?.label,
                },
                productInfo: {
                    designer: recommendedProduct?.designer,
                    designerID: recommendedProduct?.designerIdentifier,
                    name: recommendedProduct?.description,
                    productID: recommendedProduct?.partNumber,
                    ...(gender ? { gender } : {}),
                },
            },
            getItem(crossSellItem)?.[0],
        ],
    };
};

export const trackAddToBag = (analytics, sizeSku, sizeSelectionLabel, quantity = 1, internationalSizes) => {
    const { category = {} } = analytics.page;
    return {
        event: 'add to cart',
        effect: 'update cart',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: getItem(analytics?.product?.[0], sizeSku, sizeSelectionLabel, true, quantity, internationalSizes),
    };
};

export const trackViewMoreNavigation = (analytics) => {
    const { category = {} } = analytics.page;
    return {
        event: 'upsell - similar products - to plp',
        effect: 'navigate',
        category: {
            primaryCategory: 'upselling',
            subCategory: category.subCategory1,
        },
        item: getItem(analytics?.product?.[0]),
    };
};

export const trackEmailUsCTA = (analytics, messageType) => {
    return {
        event: 'personal shopper click - email',
        effect: 'contact personal shopper',
        category: {
            primaryCategory: 'product page',
            subCategory: messageType,
        },
    };
};

export const trackCallUsCTA = (analytics) => {
    return {
        event: 'call customer care',
        effect: 'proceed calling the customer care',
        category: {
            primaryCategory: 'product page',
            subCategory: analytics?.page?.pageInfo?.pageName || '',
        },
    };
};
